import { useCallback, useEffect, useReducer, useRef } from 'react';
import { buildSeedColony, type Cell, GRID_SIZE } from '../demo/seed';
import { tierFor, cellIsAlive } from '../lib/survival';
import { maybeReplicate } from '../lib/replication';

type State = {
  cells: Cell[];
  tickMs: number;
  paused: boolean;
  stats: {
    alive: number;
    replications: number;
    deaths: number;
    totalEarned: number;
    deepestLineage: number;
    tvlStaked: number;
  };
};

type Action =
  | { type: 'tick' }
  | { type: 'setSpeed'; value: number }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'reset'; seed?: string }
  | { type: 'addStake'; cellId: number; amount: number };

function recomputeStats(cells: Cell[], prev: State['stats']): State['stats'] {
  let alive = 0;
  let deepest = 0;
  for (const c of cells) {
    if (!c.agent) continue;
    if (cellIsAlive(c)) {
      alive++;
      if (c.agent.generation > deepest) deepest = c.agent.generation;
    }
  }
  return { ...prev, alive, deepestLineage: deepest };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'tick': {
      const cells = state.cells.map((c) => ({ ...c, agent: c.agent ? { ...c.agent } : undefined }));
      let replications = state.stats.replications;
      let deaths = state.stats.deaths;
      let totalEarned = state.stats.totalEarned;

      // 1. revenue + decay
      for (const c of cells) {
        if (!c.agent) continue;
        // Revenue: small probability proportional to a per-cell base rate
        const baseRate = c.agent.revenue24h / (24 * 1800); // per 2s tick
        const eventChance = Math.min(0.9, baseRate * 50);
        if (Math.random() < eventChance) {
          const amount = Math.max(0.001, baseRate * (0.5 + Math.random() * 4));
          c.agent.balance += amount;
          totalEarned += amount;
          c.agent.recentEvents = [
            { ts: Date.now(), source: 'Stars', amount },
            ...c.agent.recentEvents.slice(0, 4),
          ];
        }
        // Decay
        c.agent.balance = Math.max(0, c.agent.balance - c.agent.computeCostPerTick);
        // Update sparkline
        c.agent.sparkline = [...c.agent.sparkline.slice(1), c.agent.balance];
        // Update tier
        const newTier = tierFor(c.agent.balance, c.agent.computeCostPerTick);
        if (newTier === 'dead' && cellIsAlive(c)) {
          deaths++;
          c.state = 'dead';
          c.diedAt = Date.now();
        } else if (newTier !== 'dead') {
          c.state = newTier;
        }
      }

      // 2. cooldown -> empty after 5s
      for (const c of cells) {
        if (c.state === 'dead' && c.diedAt && Date.now() - c.diedAt > 5000) {
          c.state = 'empty';
          c.agent = undefined;
          c.diedAt = undefined;
        }
      }

      // 3. replication
      for (const c of cells) {
        if (maybeReplicate(c, cells)) replications++;
      }

      const stats = recomputeStats(cells, { ...state.stats, replications, deaths, totalEarned });
      return { ...state, cells, stats };
    }
    case 'setSpeed':
      return { ...state, tickMs: action.value };
    case 'pause':
      return { ...state, paused: true };
    case 'resume':
      return { ...state, paused: false };
    case 'reset': {
      const cells = buildSeedColony(action.seed ?? String(Date.now()));
      return {
        ...state,
        cells,
        stats: recomputeStats(cells, {
          alive: 0,
          replications: 0,
          deaths: 0,
          totalEarned: 0,
          deepestLineage: 0,
          tvlStaked: state.stats.tvlStaked,
        }),
      };
    }
    case 'addStake':
      return {
        ...state,
        stats: { ...state.stats, tvlStaked: state.stats.tvlStaked + action.amount },
      };
    default:
      return state;
  }
}

function initialState(seed: string): State {
  const cells = buildSeedColony(seed);
  return {
    cells,
    tickMs: 2000,
    paused: false,
    stats: recomputeStats(cells, {
      alive: 0,
      replications: 1883,
      deaths: 557,
      totalEarned: 14221,
      deepestLineage: 7,
      tvlStaked: 2104,
    }),
  };
}

export function useAutomatonTick(seed: string = 'automaton-v1') {
  const [state, dispatch] = useReducer(reducer, seed, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let timer: number | null = null;
    function schedule() {
      if (stateRef.current.paused) {
        timer = window.setTimeout(schedule, 200);
        return;
      }
      dispatch({ type: 'tick' });
      timer = window.setTimeout(schedule, stateRef.current.tickMs);
    }
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const setSpeed = useCallback((ms: number) => dispatch({ type: 'setSpeed', value: ms }), []);
  const pause = useCallback(() => dispatch({ type: 'pause' }), []);
  const resume = useCallback(() => dispatch({ type: 'resume' }), []);
  const reset = useCallback((s?: string) => dispatch({ type: 'reset', seed: s }), []);
  const addStake = useCallback(
    (cellId: number, amount: number) => dispatch({ type: 'addStake', cellId, amount }),
    []
  );

  return { ...state, setSpeed, pause, resume, reset, addStake, GRID_SIZE };
}
